import './style.scss'
import React, { useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
    BackLink,
    Card,
    PhoneField,
    SelectField,
    Text,
    TextField,
    Button,
    Flex,
    tokens,
} from 'src/componentv2'
import {
    fontBold,
    COUNTRY_CATEGORY_OPTIONS,
    BAAS_TOGGLE_LABEL,
    BACK_LINK_COLOR,
    HEADER_TITLE_GAP,
    HEADER_ACTIONS_GAP,
    CARD_PADDING,
    CARD_TOP_MARGIN,
    PAGE_PADDING_X,
    PAGE_PADDING_Y,
} from 'src/model/v2/interface/constants'
import { CreateInstitutionProps, CreateInstitutionForm, ToggleFieldProps } from './types'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'
import {
    ALIAS_MAX_LENGTH,
    BANK_ONE_CODE_MAX_LENGTH,
    EMAIL_MAX_LENGTH,
    RuleFields,
    sanitizeBankOneCode,
    validateCreateInstitution,
} from './rules'

const ToggleField: React.FC<ToggleFieldProps> = ({ label, checked, onChange, disabled }) => (
    <div className="baas-toggle">
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            className={`baas-toggle__track ${checked ? 'is-active' : ''}`}
            disabled={disabled}
            onClick={() => onChange(!checked)}
        />
        <span className="baas-toggle__label">{label}</span>
    </div>
)

const CreateInstitution: React.FC<CreateInstitutionProps> = ({ onBack, onCreate }) => {
    const navigate = useNavigate()
    const { createInstitution } = useContext(ConfigurationApiContext)

    const [form, setForm] = useState<CreateInstitutionForm>({
        institutionName: '',
        countryCategory: 'NG',
        bankOneCode: '',
        alias: '',
        verifyMeToken: '',
        adminFirstName: '',
        adminLastName: '',
        username: '',
        adminEmail: '',
        phone: '',
        country: 'NG',
        isBaas: false,
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [touched, setTouched] = useState<Partial<Record<RuleFields, boolean>>>({})

    const set = (field: keyof typeof form) => (value: any) =>
        setForm((prev) => ({ ...prev, [field]: value }))

    // All validation lives in ./rules
    const errors = useMemo(() => validateCreateInstitution(form), [form])
    const hasErrors = Object.keys(errors).length > 0

    const markTouched = (field: RuleFields) => () =>
        setTouched((prev) => ({ ...prev, [field]: true }))

    // Only show an error once the field has been touched (or a submit was attempted)
    const errorFor = (field: RuleFields) => (touched[field] ? errors[field] : undefined)

    const handleBankOneCodeChange = (value: string) =>
        setForm((prev) => ({ ...prev, bankOneCode: sanitizeBankOneCode(value) }))

    // Prefer the caller-supplied onBack (e.g. the toggle-based
    // InstitutionCreationPage swapping back to the list in place); fall back
    // to navigating straight to the list route for callers (like
    // InstitutionListPage) that render this on its own route.
    const handleBack = () => {
        if (onBack) {
            onBack()
        } else {
            navigate('/dashboardv2/institution-list')
        }
    }

    const handleCreate = async () => {
        if (isSubmitting) return

        // Reveal every rule error on a submit attempt
        setTouched({ bankOneCode: true, alias: true, adminEmail: true })
        if (hasErrors) return

        setIsSubmitting(true)
        try {
            const countryLabel =
                COUNTRY_CATEGORY_OPTIONS.find((o) => o.value === form.countryCategory)?.label || ''

            const ok = await createInstitution({
                institutionName: form.institutionName,
                country: countryLabel,
                domainType: 'Alias',
                domain: form.alias.trim(),
                emailSenderID: form.adminEmail.trim(),
                institutionCode: form.bankOneCode,
                bankOneToken: form.verifyMeToken,
                adminFirstName: form.adminFirstName,
                adminLastName: form.adminLastName,
                adminUsername: form.username,
                adminEmail: form.adminEmail.trim(),
                adminPhoneNumber: form.phone,
                isBaas: form.isBaas,
            })

            if (!ok) return

            onCreate?.(form)

            navigate('/dashboardv2/institution-list', {
                state: { refresh: true },
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="create-institution" style={{ padding: `${PAGE_PADDING_Y} ${PAGE_PADDING_X}` }}>
            <Flex justify="space-between" items="center">
                <Flex items="center" gap={HEADER_TITLE_GAP}>
                    <BackLink onClick={handleBack} color={BACK_LINK_COLOR}>{''}</BackLink>
                    <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>
                        Create Institution
                    </Text>
                </Flex>

                <Flex items="center" gap={HEADER_ACTIONS_GAP}>
                    <ToggleField
                        label={BAAS_TOGGLE_LABEL}
                        checked={form.isBaas}
                        onChange={set('isBaas')}
                        disabled={isSubmitting}
                    />
                    <Button onClick={handleCreate} disabled={isSubmitting}>
                        {isSubmitting ? 'Creating...' : 'Create'}
                    </Button>
                </Flex>
            </Flex>

            <Card padding={CARD_PADDING} topMargin={CARD_TOP_MARGIN}>
                <Text fontSize="2.4" fontWeight="700">
                    Institution Details
                </Text>

                <div className="form-row full-width">
                    <TextField
                        label="Institution Name"
                        value={form.institutionName}
                        onChange={set('institutionName')}
                        placeholder="Enter institution name"
                    />
                </div>

                <div className="form-row">
                    <SelectField
                        label="Country Category"
                        value={form.countryCategory}
                        onChange={set('countryCategory')}
                        options={COUNTRY_CATEGORY_OPTIONS}
                    />
                    <TextField
                        label="BankOne Code"
                        value={form.bankOneCode}
                        onChange={handleBankOneCodeChange}
                        onBlur={markTouched('bankOneCode')}
                        placeholder="Enter BankOne code"
                        error={errorFor('bankOneCode')}
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '\\d*',
                            maxLength: BANK_ONE_CODE_MAX_LENGTH,
                        }}
                    />
                </div>

                <div className="form-row">
                    <TextField
                        label="Institution Alias"
                        value={form.alias}
                        onChange={set('alias')}
                        onBlur={markTouched('alias')}
                        placeholder="Enter Alias"
                        error={errorFor('alias')}
                        inputProps={{ maxLength: ALIAS_MAX_LENGTH }}
                    />
                    <TextField
                        label="VerifyMe Token"
                        value={form.verifyMeToken}
                        onChange={set('verifyMeToken')}
                        placeholder="Enter token"
                    />
                </div>

                <Text topMargin="3.2" fontWeight="600" fontSize="1.8">
                    Super Admin Details
                </Text>

                <div className="form-row">
                    <TextField
                        label="First Name"
                        value={form.adminFirstName}
                        onChange={set('adminFirstName')}
                        placeholder="First name"
                    />
                    <TextField
                        label="Last Name"
                        value={form.adminLastName}
                        onChange={set('adminLastName')}
                        placeholder="Last name"
                    />
                </div>

                <div className="form-row">
                    <TextField
                        label="Username"
                        value={form.username}
                        onChange={set('username')}
                        placeholder="Enter username"
                    />
                    <TextField
                        label="Email Address"
                        value={form.adminEmail}
                        onChange={set('adminEmail')}
                        onBlur={markTouched('adminEmail')}
                        placeholder="Email address"
                        error={errorFor('adminEmail')}
                        inputProps={{ maxLength: EMAIL_MAX_LENGTH, inputMode: 'email' }}
                    />
                </div>

                <div className="form-row full-width">
                    <PhoneField
                        label="Phone Number"
                        value={form.phone}
                        country={form.country as any}
                        onCountryChange={set('country')}
                        onChange={set('phone')}
                    />
                </div>
            </Card>
        </div>
    )
}

export default CreateInstitution